import { Box, Button, Divider, Flex, Skeleton } from '@chakra-ui/react'
import { SentryErrorComponent } from '@components/Fallback'
import { FailedToLoad } from '@components/Placeholders/FailedToLoad'
import {
  useGetProjectQuery,
  useUpdateProjectWorkflowMutation,
} from '@features/projects/api'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import WorkflowEditorContainer from '@features/workflow-editor/components/Container'
import { ToolbarControls } from '@features/workflow-editor/components/Toolbar'
import useCmxToast from '@hooks/useCmxToast'
import { projectWorkflowSchema } from '@customTypes/cloudmix/workflow'
import '@xyflow/react/dist/style.css'
import {
  useWorkflowStore,
  WorkflowState,
} from '@features/workflow-editor/hooks/useWorkflowStore'
import { FaArrowUpRightFromSquare } from 'react-icons/fa6'
import { HeadingBar } from '@components/Layouts/HeadingBar'
import { Route as RunningWorkflowSessionRoute } from '@routes/_auth/o/$orgId/projects/$projectId/$workflowSessionId/workflow'
import { ActiveProjectWrapper } from '@features/projects/hooks/useActiveProject'
import { ExportJsonButton } from '@features/workflow-editor/components/ExportJsonButton'
import { ViewModeSwitcher } from '@features/workflow-editor/components/ViewModeSwitcher'
import { shallow } from 'zustand/shallow'

const selector = (store: WorkflowState) => ({
  resetDirty: store.resetDirty,
  readOnly: store.isReadOnly,
  getCurrentApiWorkflow: store.getCurrentApiWorkflow,
  validationErrors: store.validationErrors,
  isDirty: store.isDirty,
})

const SaveWorkflowButton = ({ projectId }) => {
  const toast = useCmxToast()
  const store = useWorkflowStore(selector, shallow)

  const [updateWorkflow, { isLoading: isSaving }] =
    useUpdateProjectWorkflowMutation()

  const hasValidationErrors = Object.values(store.validationErrors).some(
    (nodeErrors) => Object.keys(nodeErrors).length > 0
  )

  const handleSave = async () => {
    if (store.readOnly) return

    const apiWorkflowPayload = store.getCurrentApiWorkflow()

    const validation = projectWorkflowSchema.safeParse(apiWorkflowPayload)
    if (!validation.success) {
      console.warn('Workflow validation failed:', validation.error.errors)
      const invalidComponentCount = new Set(
        validation.error.errors
          .filter((e) => e.path[0] === 'components' && typeof e.path[1] === 'number')
          .map((e) => e.path[1])
      ).size
      toast({
        title: 'Workflow has validation errors',
        description: invalidComponentCount > 0
          ? `${invalidComponentCount} component${invalidComponentCount === 1 ? ' has' : 's have'} errors. Select ${invalidComponentCount === 1 ? 'it' : 'them'} to see details.`
          : 'Please review the workflow configuration.',
        status: 'warning',
        duration: 5000,
      })
      return
    }

    try {
      await updateWorkflow({ projectId, workflow: validation.data }).unwrap()
      store.resetDirty()
      toast({
        title: 'Workflow saved successfully',
        status: 'success',
        duration: 3000,
      })
    } catch (err) {
      toast({
        title: 'Error saving workflow',
        description: err.message || 'An unexpected error occurred.',
        status: 'error',
        duration: 3000,
      })
    }
  }

  return (
    <Button
      onClick={handleSave}
      isDisabled={
        !store.isDirty || isSaving || hasValidationErrors || store.readOnly
      }
      isLoading={isSaving}
      loadingText="Saving..."
      colorScheme="blue"
    >
      Save Workflow Template
    </Button>
  )
}

const ActiveWorkflowLinkButton = () => {
  const { projectId, orgId } = useParams({
    from: '/_auth/o/$orgId/projects/$projectId/workflow/',
  })

  return (
    <ActiveProjectWrapper projectId={projectId}>
      {({ isRunning, workflowSessionId }) => {
        if (!isRunning || !workflowSessionId) return null

        return (
          <Link
            to={RunningWorkflowSessionRoute.to}
            params={{ orgId, projectId, workflowSessionId }}
          >
            <Button
              size="sm"
              colorScheme="secondary"
              variant="outline"
              rightIcon={<FaArrowUpRightFromSquare />}
            >
              Edit Active Workflow
            </Button>
          </Link>
        )
      }}
    </ActiveProjectWrapper>
  )
}

const ProjectWorkflowPage = () => {
  const { projectId } = useParams({
    from: '/_auth/o/$orgId/projects/$projectId/workflow/',
  })

  const { data: project, isLoading, isError } = useGetProjectQuery(projectId)

  if (isError) {
    return <FailedToLoad message={'Unable to load Workflow'} />
  }

  return (
    <>
      {project && (
        <Flex
          justify="space-between"
          align="center"
          mb={2}
          wrap="wrap"
          w="full"
        >
          <HeadingBar
            resourceId={project.projectId}
            heading={project.name}
          ></HeadingBar>
          <ViewModeSwitcher />
        </Flex>
      )}
      <Divider />

      <Box py="2" overflowX="auto" h={'92%'}>
        <Skeleton isLoaded={!isLoading} height={'95%'}>
          {project && (
            <WorkflowEditorContainer
              projectId={projectId}
              workflow={project.workflow}
              mediaFormat={project.mediaFormat}
              renderToolbar={({
                addComponent,
                undoRedo,
                createMacro,
                statusBadges,
              }: ToolbarControls) => (
                <Flex gap={2} alignItems="center" mb={1} wrap="wrap">
                  <SaveWorkflowButton projectId={projectId} />
                  {statusBadges}
                  <Box flex={1} />
                  {addComponent}
                  {undoRedo}
                  {createMacro}
                  <ExportJsonButton />
                  <Box flex={1} />
                  <ActiveWorkflowLinkButton />
                </Flex>
              )}
            />
          )}
        </Skeleton>
      </Box>
    </>
  )
}

export const Route = createFileRoute(
  '/_auth/o/$orgId/projects/$projectId/workflow/'
)({
  component: ProjectWorkflowPage,
  errorComponent: SentryErrorComponent,
  beforeLoad: () => ({ getTitle: () => 'Workflow - Launch Template' }),
})
